import { useState, useEffect, useContext, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import NumberFormat from "react-number-format";

import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiBadge from "components/SuiBadge";
import SuiProgress from "components/SuiProgress";

import ReadMore from "components/UpStyled/ReadMore";

import { Upload, Empty } from "antd";
import { getBase64, dataURLtoFile, handleUploadFiles, beforeUpload } from "utils/imageHelper";

import { isInPortfolio, checkPermission } from "utils/helper";
import DropZone from "components/UpStyled/DropZone";

import moment from "moment";

import toast from "react-hot-toast";
import api from "services/api";

import CategoryContext from "context/category-context";
import SharedContentContext from "context/shared-content";
import { AccountContext } from "context/account";

import { seasonOptions } from "utils/data";
import FilePreviewCard from "components/UpStyled/FilePreviewCard";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import Comments from "pages/comment";
import Prompt from "components/UpStyled/Prompt";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import CreateColorModal from "pages/de/palette/components/CreateColorModal";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { extractColors } from "extract-colors";
import colors from "assets/theme/base/colors";
import LinearProgress, { linearProgressClasses } from "@mui/material/LinearProgress";
import { styled } from "@mui/material/styles";
import PostCard from "pages/de/posts/components/PostCard";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import PostDetailModal from "pages/de/posts/detail";
import Rating from "@mui/material/Rating";

const STYLE_DESC_MAX_LENGTH = 2000;
const MAX_STYLE_NUMBER_COUNT = 12;
const MAX_TAG_LENGTH = 40;

const MarketResearch = ({ detail, handleRefresh, accountType, sharedView = false }) => {
  const [imageFiles, setImageFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [detailImage, setDetailImage] = useState(null);
  const [showLargeDetailImage, setShowLargeDetailImage] = useState(false);
  const [imgColors, setImgColors] = useState([]);
  const [avgScore, setAvgScore] = useState({});
  const [similarItems, setSimilarItems] = useState([]);
  const [aiScore, setAiScore] = useState([]);
  const [loadingSimliarItems, setLoadingSimilarItems] = useState(false);

  const account = useContext(AccountContext);

  useEffect(() => {
    if (detail?.id) {
      handleReset();
      retrieveAvgScore();
      retrieveSimilarItems();
    }
  }, [detail.id]);

  useEffect(() => {
    retrieveColorMap();
  }, [detailImage]);

  const retrieveColorMap = async () => {
    const src = detailImage?.url || detailImage?.preview;
    if (src) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        var canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;
        canvas.width = width;
        canvas.height = height;

        var ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const data = ctx.getImageData(0, 0, width, height).data; // Uint8ClampedArray

        extractColors({ data, width, height })
          .then((res) => {
            setImgColors(res);
          })
          .catch((err) => console.log(err));
      };
      img.onerror = (error) => console.log(error);
      img.src = src;
    }
  };

  const updateImageFiles = async (files) => {
    setUploading(true);
    try {
      const myImageFiles = await handleUploadFiles(files);
      const payload = {
        style: {
          ...detail?.style,
          imageFiles: myImageFiles,
        },
      };
      await api.products.update(detail.id, payload);
      handleRefresh();

      setUploading(false);
    } catch (err) {
      setUploading(false);
      if (err?.response?.status == 401) {
        return toast.error(err?.response?.data?.error || err?.response?.statusText);
      }
      toast.error("Upload image files failed!");
    }
  };

  const retrieveAvgScore = async () => {
    try {
      const { data } = await api.score.retrieveAvgScore(detail.id);
      setAvgScore(data?.score || {});
    } catch (err) {
      //console.log(err);
    }
  };

  const retrieveSimilarItems = async () => {
    try {
      const styleImages = detail?.style?.imageFiles ? detail.style.imageFiles : [];
      if (styleImages.length > 0) {
        setLoadingSimilarItems(true);
        const category = detail?.type?.value || "";
        const payload = {
          task_type: "imagescoring",
          task_meta: {
            conditional: styleImages[0]?.url,
            category: category.toLowerCase(),
          },
        };
        const { data } = await api.ailab.sendJob(payload);
        const img_urls = data?.results?.img_urls || "";
        const products = data?.results?.urls || "";
        const scores = data?.results?.scores || "";

        let images = img_urls.split(",");
        let productUrls = products.split(",");
        let imgScores = scores.split(",");
        images = images.filter((x) => x != "");
        productUrls = productUrls.filter((x) => x != "");
        imgScores = imgScores.filter((x) => x != "");

        if (images.length > 0 && images.length == productUrls.length) {
          let items = [];
          for (let i = 0; i < images.length; i++) {
            items.push({ img_url: images[i], url: productUrls[i] });
          }

          setSimilarItems(items);
        }
        setAiScore(imgScores);
        setLoadingSimilarItems(false);
      }
    } catch (err) {
      console.log(err);
      setLoadingSimilarItems(false);
    }
  };

  const handleReset = () => {
    const styleMeta = detail?.style ? detail.style : {};

    setImageFiles(styleMeta?.imageFiles || []);
    if (styleMeta?.imageFiles && styleMeta?.imageFiles.length > 0) {
      setDetailImage(styleMeta.imageFiles[0]);
    }
  };

  const handleUploadImages = (data) => {
    updateImageFiles([data.file]);

    // return api.uploads.create([file])
  };

  let canWrite = accountType == "de" && !sharedView; //won't allow anyone to change the data
  //||
  // (sharedView && checkPermission(detail?.id, detail?.collection_id, permissionMap));

  //check if the style is locked
  if (detail?.mode == 2) {
    canWrite = false;
  }

  return (
    <SuiBox>
      <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={uploading}>
        <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
        <SuiTypography variant="h6" fontWeight="medium" color="info">
          Uploading supporting files...
        </SuiTypography>
      </Backdrop>
      <SuiBox>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={5}>
            <Card sx={{ p: 3, height: "100%" }}>
              <SuiBox>
                <SuiBox ml={0.5}>
                  <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                    Style Images
                  </SuiTypography>
                </SuiBox>
                <SuiBox mb={1.5} ml={0.5} mt={0.5} lineHeight={0} display="inline-block">
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="regular"
                    color="text"
                  >
                    The first image will be your primary image
                  </SuiTypography>
                </SuiBox>
                <SuiBox sx={{ maxWidth: 320, margin: "auto" }}>
                  {imageFiles && imageFiles.length > 0 ? (
                    <div style={{ position: "relative" }}>
                      <SuiButton
                        variant="contained"
                        iconOnly
                        circular
                        sx={{
                          position: "absolute",
                          top: "50%",
                          right: 0,
                          zIndex: 4,
                          minWidth: "2.5rem",
                          opacity: 0.6,
                          "&:hover": { opacity: 0.9 },
                        }}
                        onClick={() => {
                          const index = imageFiles.findIndex((x) =>
                            x.preview ? x.preview == detailImage.preview : x.url == detailImage.url
                          );
                          if (index > -1) {
                            if (index < imageFiles.length - 1) {
                              setDetailImage(imageFiles[index + 1]);
                            }
                          }
                        }}
                      >
                        <ArrowForwardIosIcon />
                      </SuiButton>
                      <SuiButton
                        variant="contained"
                        iconOnly
                        circular
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: 0,
                          zIndex: 4,
                          minWidth: "2.5rem",
                          opacity: 0.6,
                          textAlign: "center",
                          margin: "auto",
                          "&:hover": { opacity: 0.9 },
                        }}
                        onClick={() => {
                          const index = imageFiles.findIndex((x) =>
                            x.preview ? x.preview == detailImage.preview : x.url == detailImage.url
                          );
                          if (index > -1) {
                            if (index > 0) {
                              setDetailImage(imageFiles[index - 1]);
                            }
                          }
                        }}
                      >
                        <ArrowBackIosNewIcon />
                      </SuiButton>
                      <SuiBox
                        onClick={() => setShowLargeDetailImage(true)}
                        sx={{ cursor: "pointer" }}
                      >
                        <ImageCardWrapper
                          data={detailImage}
                          mode={"normal"}
                          width={320}
                          height={400}
                          borderRadius="xl"
                          pdfPagination={true}
                        />
                      </SuiBox>
                    </div>
                  ) : (
                    <Upload.Dragger
                      name="design-images"
                      showUploadList={false}
                      customRequest={handleUploadImages}
                      accept="image/*, application/pdf"
                      className="rounded-2xl h-full"
                    >
                      <SuiBox
                        sx={{
                          overflow: "hidden",
                          width: "100%",
                          backgroundImage: ({
                            functions: { rgba, linearGradient },
                            palette: { gradients },
                          }) =>
                            `${linearGradient(
                              rgba(gradients.secondary.main, 0.2),
                              rgba(gradients.secondary.state, 0.4)
                            )}`,
                          height: 400,
                          margin: "-16px 0px",
                          boxShadow: ({ boxShadows: { md } }) => md,
                          objectFit: "contain",
                          objectPosition: "center",
                        }}
                        display="flex"
                        alignItems="center"
                        position="relative"
                        minHeight="400px"
                        borderRadius="xl"
                      />
                    </Upload.Dragger>
                  )}
                </SuiBox>
              </SuiBox>
              <SuiBox mt={2}>
                {canWrite ? (
                  <DropZone
                    files={imageFiles}
                    setfiles={setImageFiles}
                    options={{
                      accept: { "image/*": [".jpeg", ".png"], "application/pdf": [".pdf"] },
                    }}
                    handleOnChange={(fileList) => {
                      updateImageFiles(fileList);
                    }}
                    onFileClick={(file) => setDetailImage(file)}
                  />
                ) : (
                  <div
                    style={{
                      border: "2px dashed gray",
                      borderRadius: "15px",
                      margin: "0 4px",
                      width: "100%",
                      height: "100%",
                      overflowX: "auto",
                      overflowY: "hidden",
                      padding: "8px",
                    }}
                  >
                    {imageFiles && imageFiles.length > 0 ? (
                      <FilePreviewCard
                        files={imageFiles}
                        setfiles={false}
                        handleDelete={false}
                        onFileClick={(file) => setDetailImage(file)}
                      />
                    ) : (
                      <SuiBox sx={{ height: 140, px: 2 }} display="flex" alignItems="center">
                        <SuiTypography
                          variant="h6"
                          color="text"
                          fontWeight="regular"
                          textTransform="capitalize"
                        >
                          No available data!
                        </SuiTypography>
                      </SuiBox>
                    )}
                  </div>
                )}
              </SuiBox>
            </Card>
          </Grid>

          <Grid item xs={12} sm={12} md={7}>
            <Card sx={{ p: 3, height: "100%" }}>
              <SuiBox>
                <SuiBox ml={0.5}>
                  <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                    Market Research
                  </SuiTypography>
                </SuiBox>
                <SuiBox mb={1.5} ml={0.5} mt={0.5} lineHeight={0}>
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="regular"
                    color="text"
                  >
                    Discover similar styles from around the industry
                  </SuiTypography>
                </SuiBox>
              </SuiBox>
              <SuiBox>
                <SuiBox ml={0.5}>
                  <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                    Colorway
                  </SuiTypography>
                </SuiBox>
                <SuiBox
                  mb={1.5}
                  ml={0.5}
                  mt={0.5}
                  lineHeight={0}
                  display="flex"
                  gap={2}
                  sx={{ flexWrap: "nowarp", overflow: "auto" }}
                >
                  {imgColors.map((item) => (
                    <SuiBox sx={{ textAlign: "center", minWidth: 100 }}>
                      <SuiBox
                        sx={{
                          height: 160,
                          width: 100,
                          minWidth: 100,
                          backgroundColor: item.hex,
                          border: `${colors.secondary.main} solid 1px`,
                          borderRadius: "8px",
                        }}
                      />
                      <SuiTypography variant="body1" textTransform="uppercase" color="text">
                        {item.hex}
                      </SuiTypography>
                    </SuiBox>
                  ))}
                </SuiBox>
              </SuiBox>
              <SuiBox my={1}>
                <SuiBox ml={0.5}>
                  <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                    Community feedback
                  </SuiTypography>
                </SuiBox>
                <SuiBox>
                  <Grid container>
                    <Grid item lg={3} md={4} sm={4} xs={4} sx={{ px: 2 }}>
                      <Tooltip title={`${avgScore?.popularity_count || 0} people voted`}>
                        <SuiTypography
                          variant="body2"
                          fontWeight="bold"
                          textTransform="capitalize"
                          color="text"
                          sx={{ textAlign: "start" }}
                        >
                          Trendiness({avgScore?.trendiness_count || 0})
                        </SuiTypography>
                      </Tooltip>
                      <SuiBox display="flex" alignItems="center" gap={0.5} flexWrap="nowrap">
                        {/* <Rating
                          name="trendiness"
                          defaultValue={avgScore?.trendiness_count}
                          size="small"
                          readOnly
                        />{" "} */}
                        <SuiBox width="100%">
                          <SuiProgress
                            variant="determinate"
                            value={
                              avgScore?.avg_trendiness ? avgScore?.avg_trendiness * 20 : undefined
                            }
                          />
                        </SuiBox>
                        <SuiTypography
                          variant="body2"
                          color="text"
                          sx={{ minWidth: 35, wordBreak: "keep-all", whiteSpace: "nowrap" }}
                        >
                          {avgScore?.avg_trendiness
                            ? `(${avgScore?.avg_trendiness.toFixed(1)})`
                            : undefined}
                        </SuiTypography>
                      </SuiBox>
                    </Grid>
                    <Grid item lg={3} md={4} sm={4} xs={4} sx={{ px: 2 }}>
                      <Tooltip title={`${avgScore?.popularity_count || 0} people voted`}>
                        <SuiTypography
                          variant="body2"
                          fontWeight="bold"
                          textTransform="capitalize"
                          color="text"
                          sx={{ textAlign: "start" }}
                        >
                          Uniqueness({avgScore?.popularity_count || 0})
                        </SuiTypography>
                      </Tooltip>
                      <SuiBox display="flex" alignItems="center" gap={0.5} flexWrap="nowrap">
                        {/* <Rating
                          name="uniqueness"
                          value={avgScore?.avg_uniqueness}
                          size="small"
                          readOnly
                        />{" "} */}
                        <SuiBox width="100%">
                          <SuiProgress
                            variant="determinate"
                            value={
                              avgScore?.avg_uniqueness ? avgScore?.avg_uniqueness * 20 : undefined
                            }
                          />
                        </SuiBox>
                        <SuiTypography
                          variant="body2"
                          color="text"
                          sx={{ minWidth: 35, wordBreak: "keep-all", whiteSpace: "nowrap" }}
                        >
                          {avgScore?.avg_uniqueness
                            ? `(${avgScore?.avg_uniqueness.toFixed(1)})`
                            : undefined}
                        </SuiTypography>
                      </SuiBox>
                    </Grid>
                    <Grid item lg={3} md={4} sm={4} xs={4} sx={{ px: 2 }}>
                      <Tooltip title={`${avgScore?.popularity_count || 0} people voted`}>
                        <SuiTypography
                          variant="body2"
                          fontWeight="bold"
                          textTransform="capitalize"
                          color="text"
                          sx={{ textAlign: "start" }}
                        >
                          Popularity({avgScore?.popularity_count || 0})
                        </SuiTypography>
                      </Tooltip>
                      <SuiBox display="flex" alignItems="center" gap={0.5} flexWrap="nowrap">
                        {/* <Rating
                          name="popularity"
                          defaultValue={avgScore?.avg_popularity}
                          size="small"
                          readOnly
                        />{" "} */}
                        <SuiBox width="100%">
                          <SuiProgress
                            variant="determinate"
                            value={
                              avgScore?.avg_popularity ? avgScore?.avg_popularity * 20 : undefined
                            }
                          />
                        </SuiBox>
                        <SuiTypography
                          variant="body2"
                          color="text"
                          sx={{ minWidth: 35, wordBreak: "keep-all", whiteSpace: "nowrap" }}
                        >
                          {avgScore?.avg_popularity
                            ? `(${avgScore?.avg_popularity.toFixed(1)})`
                            : undefined}
                        </SuiTypography>
                      </SuiBox>
                    </Grid>
                  </Grid>
                </SuiBox>
              </SuiBox>
              <SuiBox my={1}>
                <SuiBox ml={0.5}>
                  <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                    AI Score
                  </SuiTypography>
                </SuiBox>
                <SuiBox>
                  <Grid container>
                    <Grid item lg={3} md={4} sm={4} xs={4} sx={{ px: 2 }}>
                      <SuiTypography
                        variant="body2"
                        fontWeight="bold"
                        textTransform="capitalize"
                        color="text"
                        sx={{ textAlign: "start" }}
                      >
                        Trendiness
                      </SuiTypography>
                      <SuiBox display="flex" alignItems="center" gap={0.5} flexWrap="nowrap">
                        <SuiBox width="100%">
                          <SuiProgress
                            variant="determinate"
                            value={aiScore?.[2] ? aiScore?.[2] * 20 : undefined}
                          />
                        </SuiBox>
                        <SuiTypography
                          variant="body2"
                          color="text"
                          sx={{ minWidth: 35, wordBreak: "keep-all", whiteSpace: "nowrap" }}
                        >
                          {aiScore?.[2] ? `(${aiScore?.[2]})` : undefined}
                        </SuiTypography>
                      </SuiBox>
                    </Grid>
                    <Grid item lg={3} md={4} sm={4} xs={4} sx={{ px: 2 }}>
                      <SuiTypography
                        variant="body2"
                        fontWeight="bold"
                        textTransform="capitalize"
                        color="text"
                        sx={{ textAlign: "start" }}
                      >
                        Uniqueness
                      </SuiTypography>

                      <SuiBox display="flex" alignItems="center" gap={0.5} flexWrap="nowrap">
                        <SuiBox width="100%">
                          <SuiProgress
                            variant="determinate"
                            value={aiScore?.[1] ? aiScore?.[1] * 20 : undefined}
                          />
                        </SuiBox>
                        <SuiTypography
                          variant="body2"
                          color="text"
                          sx={{ minWidth: 35, wordBreak: "keep-all", whiteSpace: "nowrap" }}
                        >
                          {aiScore?.[1] ? `(${aiScore?.[1]})` : undefined}
                        </SuiTypography>
                      </SuiBox>
                    </Grid>
                    <Grid item lg={3} md={4} sm={4} xs={4} sx={{ px: 2 }}>
                      <SuiTypography
                        variant="body2"
                        fontWeight="bold"
                        textTransform="capitalize"
                        color="text"
                        sx={{ textAlign: "start" }}
                      >
                        Popularity
                      </SuiTypography>
                      <SuiBox display="flex" alignItems="center" gap={0.5} flexWrap="nowrap">
                        <SuiBox width="100%">
                          <SuiProgress
                            variant="determinate"
                            value={aiScore?.[0] ? aiScore?.[0] * 20 : undefined}
                          />
                        </SuiBox>
                        <SuiTypography
                          variant="body2"
                          color="text"
                          sx={{ minWidth: 35, wordBreak: "keep-all", whiteSpace: "nowrap" }}
                        >
                          {aiScore?.[0] ? `(${aiScore?.[0]})` : undefined}
                        </SuiTypography>
                      </SuiBox>
                    </Grid>
                  </Grid>
                </SuiBox>
              </SuiBox>

              <SuiBox sx={{ my: 1 }}>
                <SuiBox ml={0.5}>
                  <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                    Similar Styles in Stores Now
                  </SuiTypography>
                </SuiBox>

                {loadingSimliarItems ? (
                  <SuiBox display="flex" alignItems="center" justifyContent="Center" mt={3}>
                    <SuiBox display="flex" gap={0.5} alignItems="center">
                      <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
                      <SuiTypography variant="h6" fontWeight="medium" color="info">
                        Loading...
                      </SuiTypography>
                    </SuiBox>
                  </SuiBox>
                ) : (
                  <SuiBox sx={{ maxHeight: 500, overflow: "auto" }}>
                    {similarItems.length > 0 ? (
                      <ResponsiveMasonry
                        columnsCountBreakPoints={{ 250: 1, 400: 2, 650: 2, 1050: 3, 1400: 4 }}
                      >
                        <Masonry>
                          {similarItems.map((item, index) => (
                            <SuiBox sx={{ px: 2 }} component="a" href={item.url} target="_blank">
                              <img
                                style={{ width: "100%", height: "auto" }}
                                alt={"similar_" + index}
                                src={item.img_url}
                                crossorigin
                              />
                            </SuiBox>
                          ))}
                        </Masonry>
                      </ResponsiveMasonry>
                    ) : (
                      <SuiBox mt={3}>
                        <Empty />
                      </SuiBox>
                    )}
                  </SuiBox>
                )}
              </SuiBox>
            </Card>
          </Grid>
        </Grid>
      </SuiBox>
      {showLargeDetailImage ? (
        <Prompt
          visible={showLargeDetailImage}
          handleClose={() => setShowLargeDetailImage(false)}
          handleOk={() => setShowLargeDetailImage(false)}
          option={{ maxWidth: "xl" }}
          text={
            <SuiBox pt={1}>
              <ImageCardWrapper
                data={detailImage}
                mode={"normal"}
                sx={{ maxHeight: 640, minHeight: 300 }}
                isOriginalSize
                pdfPagination={true}
              />
            </SuiBox>
          }
          hideAction
        />
      ) : null}
    </SuiBox>
  );
};

export default MarketResearch;
